import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiBusinessPhotoGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-business-photo-generator',
			name: 'AI Business Photo Generator — Create Professional Headshots | Lift',
			description:
				'Generate professional business headshots instantly with AI. No watermark. Upload selfies, choose your style, and get studio-quality business photos in minutes. Try now!',
			url: 'https://liftapp.ai/ai-business-photo-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-business-photo-generator#product' },
				{ '@id': 'https://liftapp.ai/ai-business-photo-generator#howto' },
				{ '@id': 'https://liftapp.ai/ai-business-photo-generator#benefits' },
				{ '@id': 'https://liftapp.ai/ai-business-photo-generator#faq' },
				{ '@id': 'https://liftapp.ai/ai-business-photo-generator#why-lift' },
				{
					'@id': 'https://liftapp.ai/ai-business-photo-generator#explore-tools',
				},
				{ '@id': 'https://liftapp.ai/ai-business-photo-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-business-photo-generator#product',
			name: 'AI Business Photo Generator',
			description:
				'Create professional business headshots in minutes without a photo shoot. Get studio-quality corporate photos with multiple styles and backgrounds, no watermarks.',
			url: 'https://liftapp.ai/ai-business-photo-generator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-business-photo-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift generates professional business headshots automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-business-photo-generator#benefits',
			name: 'Who can benefit from AI Business Photo Generator?',
			description:
				"Lift's AI Business Photo Generator is useful for professionals, creators, and businesses who want clean, distraction-free images for social media, e-commerce, marketing, or personal use.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Job Seekers & Career Switchers — Boost your LinkedIn profile and resume with professional headshots that increase credibility and interview callbacks. Get business-ready photos fast without expensive studio sessions.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Freelancers & Solopreneurs — Build a strong personal brand with consistent, high-quality business photos for your website, social profiles, and pitch decks. Save thousands on photographers while getting multiple professional styles.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: "Remote Teams — Unify your company's visual identity with consistent, professional headshots for all team members. No scheduling hassles, no location barriers — just upload selfies and get uniform business photos for your About Us page.",
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Personal Brand Builders — Stand out with polished, professional headshots that elevate your online presence across all platforms. Experiment with different styles and backgrounds to find what best represents your brand.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Create professional-looking business photos for academic profiles, conference presentations, and job applications. Affordable alternative to expensive photo shoots for emerging professionals.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Business profile picture use cases',
				description: 'Business profile picture use cases',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-business-photo-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Business Photo Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Business Photo Generator is a premium online tool that instantly creates professional business headshots with AI precision — ideal for LinkedIn profiles, corporate websites, email signatures, and professional branding.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Business Photo Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload a few selfies to Lift's AI headshot generator tool. Our AI automatically generates professional business photos with your chosen style, outfit, and background in minutes — no photography experience needed. Get studio-quality headshots without a physical photo shoot.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Business Photo Generator uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking cutouts without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-business-photo-generator#why-lift',
			name: "Why Lift's AI Business Photo Generator stands out",
			description:
				"Lift's Business Photo Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'AI work photo generator',
				description: 'AI work photo generator',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-business-photo-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-business-photo-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Business Photo Generator',
					item: 'https://liftapp.ai/ai-business-photo-generator',
				},
			],
		},
	],
};
